import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { AgentSummary } from '@t3/shared';
import { Clock } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { FormField } from '../components/FormField.tsx';
import { Select } from '../components/Select.tsx';
import { RunningTimersPanel } from '../components/RunningTimersPanel.tsx';
import { TimeEntryEditDialog } from '../components/TimeEntryEditDialog.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';
import type { BillingBucket } from '../timer/buckets.ts';

interface Entry {
  id: string;
  ticketId: string | null;
  agentId: string;
  agentName: string | null;
  startedAt: string;
  durationMinutes: number;
  notes: string | null;
  billingBucket: BillingBucket;
  isBilled: boolean;
  voidedAt: string | null;
  ticketDisplayNumber: number | null;
  ticketSubject: string | null;
  organizationName: string | null;
}

interface SummaryTech {
  agentId: string;
  agentName: string;
  billableMinutes: number;
  nonBillableMinutes: number;
  totalMinutes: number;
}

interface CompTimeBalance {
  balanceHours: number;
  capHours: number;
  totalAccruedHours: number;
  totalUsedHours: number;
  forfeitedHours: number;
  monthStart: string;
  monthEnd: string;
}

interface CompTimeUsage {
  id: string;
  agentId: string;
  usedOn: string;
  hours: number;
  notes: string | null;
}

function startOfWeek(d = new Date()): Date {
  const out = new Date(d.toDateString());
  out.setDate(out.getDate() - out.getDay());
  return out;
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function TimesheetsPage(): React.ReactElement {
  useDocumentTitle('Timesheets');
  const { agent } = useAuth();
  // `admin` and `time_admin` both have full mutation rights on this page;
  // the variable name stays `isAdmin` because every gate below is a
  // time-tracking gate (filter any agent, add/edit/restore any entry).
  const isAdmin = agent?.role === 'admin' || agent?.role === 'time_admin';
  const [from, setFrom] = useState<string>(ymd(startOfWeek()));
  const [to, setTo] = useState<string>(ymd(new Date(Date.now() + 24 * 60 * 60 * 1000)));
  const [agentFilter, setAgentFilter] = useState<string>('');
  const [bucket, setBucket] = useState<string>('');
  const [includeVoided, setIncludeVoided] = useState(false);
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  // null => no dialog. { entry: null } => add mode. { entry } => edit mode.
  const [dialog, setDialog] = useState<{ entry: Entry | null } | null>(null);

  const apiQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (from) p.set('from', new Date(from).toISOString());
    if (to) p.set('to', new Date(to).toISOString());
    if (isAdmin && agentFilter) p.set('agentId', agentFilter);
    if (bucket) p.set('bucket', bucket);
    if (includeVoided) p.set('includeVoided', 'true');
    return p.toString();
  }, [from, to, agentFilter, bucket, includeVoided, isAdmin]);

  async function load() {
    try {
      const res = await apiFetch<{ entries: Entry[] }>(`/api/timesheets?${apiQuery}`);
      setEntries(res.entries);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load timesheet.');
    }
  }

  useEffect(() => {
    void load();
  }, [apiQuery]);

  useEffect(() => {
    if (!isAdmin) return;
    apiFetch<{ agents: AgentSummary[] }>('/api/agents')
      .then((r) => setAgents(r.agents))
      .catch(() => setAgents([]));
  }, [isAdmin]);

  const grouped = useMemo(() => groupByDate(entries ?? []), [entries]);
  const totalMinutes = (entries ?? [])
    .filter((e) => !e.voidedAt)
    .reduce((sum, e) => sum + e.durationMinutes, 0);

  if (!agent) return <p>Loading…</p>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[var(--color-header)]">Timesheets</h1>
          <p className="mt-1 text-[var(--color-muted)]">
            {isAdmin ? 'All agents · ' : ''}
            {(totalMinutes / 60).toFixed(2)}h total in range
          </p>
        </div>
        <div className="flex gap-2">
          <a href={`/api/timesheets/export.csv?${apiQuery}`}>
            <Button variant="outline">Export CSV</Button>
          </a>
          <Button onClick={() => setDialog({ entry: null })}>Add entry</Button>
        </div>
      </div>

      {isAdmin && <RunningTimersPanel />}

      {isAdmin && <HoursByTechCard />}

      <CompTimeCard isAdmin={isAdmin} myAgentId={agent.id} viewingAgentId={isAdmin ? agentFilter || undefined : agent.id} />

      <Card>
        <div className="grid gap-3 sm:grid-cols-5">
          <FormField label="From" name="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <FormField label="To" name="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          {isAdmin && (
            <Select label="Agent" value={agentFilter} onChange={(e) => setAgentFilter(e.target.value)}>
              <option value="">All agents</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </Select>
          )}
          <Select label="Bucket" value={bucket} onChange={(e) => setBucket(e.target.value)}>
            <option value="">All buckets</option>
            <option value="block_hours">Block hours</option>
            <option value="project">Project</option>
            <option value="quote">Quote</option>
            <option value="chat_assist">Chat assistance</option>
            <option value="non_billable_pm">Non-billable PM (check-ins, onboarding)</option>
            <option value="non_billable_internal">Non-billable internal (team, learning)</option>
            <option value="comp_time_used">Comp time used</option>
            <option value="non_billable">Non-billable (legacy)</option>
          </Select>
          <label className="flex items-end gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeVoided}
              onChange={(e) => setIncludeVoided(e.target.checked)}
              className="h-4 w-4 accent-[var(--color-t3-blue)]"
            />
            Include voided
          </label>
        </div>
      </Card>

      {dialog && (
        <TimeEntryEditDialog
          mode={dialog.entry ? 'edit' : 'add'}
          isAdmin={isAdmin}
          meId={agent.id}
          initial={dialog.entry ?? undefined}
          onSaved={load}
          onClose={() => setDialog(null)}
        />
      )}

      {error && (
        <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">{error}</p>
        </Card>
      )}

      {entries === null ? (
        <p className="text-[var(--color-muted)]">Loading…</p>
      ) : entries.length === 0 ? (
        <EmptyState
          icon={<Clock className="h-8 w-8" />}
          title="No time logged in this range"
          body="Start a timer on a ticket, or click 'Add entry' above to log time manually."
        />
      ) : (
        Object.entries(grouped).map(([date, list]) => {
          const dayMinutes = list.filter((e) => !e.voidedAt).reduce((s, e) => s + e.durationMinutes, 0);
          return (
            <div key={date}>
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--color-muted)]">
                  {new Date(date).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
                </h2>
                <span className="text-sm font-semibold text-[var(--color-header)]">
                  {(dayMinutes / 60).toFixed(2)}h
                </span>
              </div>
              <div className="overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                    <tr>
                      <th className="px-4 py-2">Time</th>
                      {isAdmin && <th className="px-4 py-2">Agent</th>}
                      <th className="px-4 py-2">Ticket</th>
                      <th className="px-4 py-2">Notes</th>
                      <th className="px-4 py-2">Bucket</th>
                      <th className="px-4 py-2 text-right">Duration</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((e) => (
                      <EntryRow
                        key={e.id}
                        entry={e}
                        isAdmin={isAdmin}
                        meId={agent.id}
                        onChanged={load}
                        onError={setError}
                        onOpenDialog={(entry) => setDialog({ entry })}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function groupByDate(entries: Entry[]): Record<string, Entry[]> {
  const out: Record<string, Entry[]> = {};
  for (const e of entries) {
    const key = ymd(new Date(e.startedAt));
    (out[key] ??= []).push(e);
  }
  return out;
}

function EntryRow({
  entry,
  isAdmin,
  meId,
  onChanged,
  onError,
  onOpenDialog,
}: {
  entry: Entry;
  isAdmin: boolean;
  meId: string;
  onChanged: () => Promise<void> | void;
  onError: (s: string) => void;
  onOpenDialog: (entry: Entry) => void;
}): React.ReactElement {
  const [editing, setEditing] = useState(false);
  const [duration, setDuration] = useState(String(entry.durationMinutes));
  const [notes, setNotes] = useState(entry.notes ?? '');
  const [busy, setBusy] = useState(false);
  const canMutate = !entry.voidedAt && (entry.agentId === meId || isAdmin);
  const canRestore = isAdmin && !!entry.voidedAt;

  async function save() {
    setBusy(true);
    try {
      await apiFetch(`/api/timesheets/${entry.id}`, {
        method: 'PATCH',
        json: { durationMinutes: Number(duration), notes: notes || null },
      });
      await onChanged();
      setEditing(false);
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  async function voidEntry() {
    if (!confirm('Void this entry? It will not count toward billing.')) return;
    setBusy(true);
    try {
      await apiFetch(`/api/timesheets/${entry.id}`, { method: 'DELETE' });
      await onChanged();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Void failed.');
    } finally {
      setBusy(false);
    }
  }

  async function restoreEntry() {
    setBusy(true);
    try {
      await apiFetch(`/api/timesheets/${entry.id}/unvoid`, { method: 'POST' });
      await onChanged();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Restore failed.');
    } finally {
      setBusy(false);
    }
  }

  const t = new Date(entry.startedAt);
  const time = t.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  return (
    <tr className={'border-t border-[var(--color-border)] ' + (entry.voidedAt ? 'bg-[var(--color-surface)]/40 text-[var(--color-muted)] line-through' : '')}>
      <td className="px-4 py-2 text-xs">{time}</td>
      {isAdmin && <td className="px-4 py-2 text-xs">{entry.agentName ?? '—'}</td>}
      <td className="px-4 py-2">
        {entry.ticketId && entry.ticketDisplayNumber !== null ? (
          <>
            <Link to={`/tickets/${entry.ticketId}`} className="text-[var(--color-t3-blue)] hover:underline">
              T-{entry.ticketDisplayNumber}
            </Link>
            <div className="text-xs text-[var(--color-muted)]">{entry.organizationName ?? ''} · {entry.ticketSubject ?? ''}</div>
          </>
        ) : (
          <span className="text-xs italic text-[var(--color-muted)]">internal — no ticket</span>
        )}
      </td>
      <td className="px-4 py-2 text-xs">
        {editing ? (
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded border border-[var(--color-border)] px-2 py-1"
          />
        ) : (
          entry.notes ?? '—'
        )}
      </td>
      <td className="px-4 py-2 text-xs uppercase tracking-wide">{entry.billingBucket.replace(/_/g, ' ')}</td>
      <td className="px-4 py-2 text-right">
        {editing ? (
          <input
            type="number"
            value={duration}
            min={1}
            max={24 * 60}
            onChange={(e) => setDuration(e.target.value)}
            className="w-20 rounded border border-[var(--color-border)] px-2 py-1 text-right"
          />
        ) : (
          `${(entry.durationMinutes / 60).toFixed(2)}h`
        )}
      </td>
      <td className="px-4 py-2 text-right">
        {canRestore ? (
          <button
            type="button"
            onClick={() => void restoreEntry()}
            disabled={busy}
            className="text-xs text-[var(--color-t3-blue)] hover:underline"
          >
            Restore
          </button>
        ) : canMutate ? (
          editing ? (
            <div className="flex justify-end gap-1">
              <button
                type="button"
                onClick={() => void save()}
                disabled={busy}
                className="text-xs text-[var(--color-t3-blue)] hover:underline"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                disabled={busy}
                className="text-xs text-[var(--color-muted)] hover:underline"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex justify-end gap-2">
              {isAdmin ? (
                <button
                  type="button"
                  onClick={() => onOpenDialog(entry)}
                  className="text-xs text-[var(--color-t3-blue)] hover:underline"
                >
                  Edit
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="text-xs text-[var(--color-t3-blue)] hover:underline"
                >
                  Edit
                </button>
              )}
              <button
                type="button"
                onClick={() => void voidEntry()}
                className="text-xs text-[var(--color-danger)] hover:underline"
              >
                Void
              </button>
            </div>
          )
        ) : null}
      </td>
    </tr>
  );
}

function HoursByTechCard(): React.ReactElement {
  const [period, setPeriod] = useState<'week' | 'month'>('month');
  const [rows, setRows] = useState<SummaryTech[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Resolve the [from, to) window and a human label for the chosen period.
  const { from, to, label } = useMemo(() => {
    const now = new Date();
    if (period === 'week') {
      const start = startOfWeek(now);
      const end = new Date(start);
      end.setDate(end.getDate() + 7);
      const endLabel = new Date(end);
      endLabel.setDate(endLabel.getDate() - 1);
      const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      return { from: start, to: end, label: `${fmt(start)} – ${fmt(endLabel)}` };
    }
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return { from: start, to: end, label: start.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) };
  }, [period]);

  useEffect(() => {
    setRows(null);
    setError(null);
    const p = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
    apiFetch<{ technicians: SummaryTech[] }>(`/api/timesheets/summary?${p.toString()}`)
      .then((r) => setRows(r.technicians))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load summary.'));
  }, [from, to]);

  const hrs = (m: number) => (m / 60).toFixed(2);
  const withTime = (rows ?? []).filter((r) => r.totalMinutes > 0);
  const totals = (rows ?? []).reduce(
    (acc, r) => ({
      billable: acc.billable + r.billableMinutes,
      nonBillable: acc.nonBillable + r.nonBillableMinutes,
      total: acc.total + r.totalMinutes,
    }),
    { billable: 0, nonBillable: 0, total: 0 },
  );

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-baseline gap-3">
          <h2 className="text-base font-semibold text-[var(--color-header)]">Hours by technician</h2>
          <span className="text-xs uppercase tracking-wide text-[var(--color-muted)]">{label}</span>
        </div>
        <div className="inline-flex overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] text-xs">
          {(['week', 'month'] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              className={
                'px-3 py-1 capitalize ' +
                (period === p
                  ? 'bg-[var(--color-t3-blue)] text-white'
                  : 'bg-white text-[var(--color-muted)] hover:bg-[var(--color-surface)]')
              }
            >
              This {p}
            </button>
          ))}
        </div>
      </div>
      {error ? (
        <p className="text-sm text-[var(--color-danger)]">{error}</p>
      ) : rows === null ? (
        <p className="text-sm text-[var(--color-muted)]">Loading…</p>
      ) : withTime.length === 0 ? (
        <p className="text-sm text-[var(--color-muted)]">No time logged this {period}.</p>
      ) : (
        <div className="overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)]">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
              <tr>
                <th className="px-4 py-2">Technician</th>
                <th className="px-4 py-2 text-right">Billable</th>
                <th className="px-4 py-2 text-right">Non-billable</th>
                <th className="px-4 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {withTime.map((r) => (
                <tr key={r.agentId} className="border-t border-[var(--color-border)]">
                  <td className="px-4 py-2">{r.agentName}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{hrs(r.billableMinutes)}h</td>
                  <td className="px-4 py-2 text-right tabular-nums text-[var(--color-muted)]">{hrs(r.nonBillableMinutes)}h</td>
                  <td className="px-4 py-2 text-right font-semibold tabular-nums">{hrs(r.totalMinutes)}h</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[var(--color-border)] bg-[var(--color-surface)] font-semibold">
                <td className="px-4 py-2">All technicians</td>
                <td className="px-4 py-2 text-right tabular-nums">{hrs(totals.billable)}h</td>
                <td className="px-4 py-2 text-right tabular-nums text-[var(--color-muted)]">{hrs(totals.nonBillable)}h</td>
                <td className="px-4 py-2 text-right tabular-nums">{hrs(totals.total)}h</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Card>
  );
}

function CompTimeCard({
  isAdmin,
  myAgentId,
  viewingAgentId,
}: {
  isAdmin: boolean;
  myAgentId: string;
  viewingAgentId: string | undefined;
}): React.ReactElement {
  const [data, setData] = useState<{ balance: CompTimeBalance; usages: CompTimeUsage[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [usedOn, setUsedOn] = useState(ymd(new Date()));
  const [hours, setHours] = useState('1');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const target = viewingAgentId ?? myAgentId;
  const canSubmit = target === myAgentId;

  async function load() {
    try {
      const qs = isAdmin && viewingAgentId ? `?agentId=${viewingAgentId}` : '';
      const r = await apiFetch<{ balance: CompTimeBalance; usages: CompTimeUsage[] }>(
        `/api/timesheets/comp-time${qs}`,
      );
      setData(r);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load comp time.');
    }
  }

  useEffect(() => {
    void load();
  }, [target]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiFetch('/api/timesheets/comp-time', {
        method: 'POST',
        json: { usedOn, hours: Number(hours), notes: notes || undefined },
      });
      setShowForm(false);
      setHours('1');
      setNotes('');
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to record comp time.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!confirm('Delete this comp-time entry?')) return;
    try {
      await apiFetch(`/api/timesheets/comp-time/${id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Delete failed.');
    }
  }

  return (
    <Card className="border-[var(--color-t3-purple)]/30 bg-purple-50/40">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-header)]">Comp time</h2>
          <p className="text-xs text-[var(--color-muted)]">
            Weekend hours accrue this month, capped at {data?.balance.capHours ?? 16}h. Use during
            the week to take time off. <strong>Resets at month-end</strong> — anything left forfeits.
          </p>
        </div>
        {data && (
          <div className="flex items-center gap-4 text-right">
            <div>
              <div className="text-2xl font-bold text-[var(--color-header)]">
                {data.balance.balanceHours.toFixed(2)}h
              </div>
              <div className="text-xs text-[var(--color-muted)]">available this month</div>
            </div>
            {canSubmit && (
              <Button onClick={() => setShowForm((s) => !s)} disabled={data.balance.balanceHours <= 0}>
                {showForm ? 'Cancel' : 'Use comp time'}
              </Button>
            )}
          </div>
        )}
      </div>
      {data && (
        <div className="mt-3 grid grid-cols-3 gap-3 text-xs text-[var(--color-muted)]">
          <Stat label="Accrued this month" value={`${data.balance.totalAccruedHours.toFixed(2)}h`} />
          <Stat label="Used this month" value={`${data.balance.totalUsedHours.toFixed(2)}h`} />
          <Stat label="Forfeited (over cap)" value={`${data.balance.forfeitedHours.toFixed(2)}h`} />
        </div>
      )}
      {showForm && canSubmit && (
        <form onSubmit={submit} className="mt-4 grid gap-3 sm:grid-cols-3">
          <FormField label="Used on" name="usedOn" type="date" value={usedOn} onChange={(e) => setUsedOn(e.target.value)} required />
          <FormField label="Hours" name="hours" type="number" step="0.25" min={0.25} max={24} value={hours} onChange={(e) => setHours(e.target.value)} required />
          <FormField label="Notes (optional)" name="notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="sm:col-span-3">
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Record usage'}
            </Button>
          </div>
        </form>
      )}
      {data && data.usages.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">Recent usage</h3>
          <ul className="mt-2 divide-y divide-[var(--color-border)] text-sm">
            {data.usages.slice(0, 8).map((u) => (
              <li key={u.id} className="flex items-center justify-between py-2">
                <div>
                  <span className="font-mono text-xs">{u.usedOn}</span>
                  <span className="ml-3">{u.hours.toFixed(2)}h</span>
                  {u.notes && <span className="ml-3 text-[var(--color-muted)]">{u.notes}</span>}
                </div>
                {canSubmit && (
                  <button
                    type="button"
                    onClick={() => void remove(u.id)}
                    className="text-xs text-[var(--color-danger)] hover:underline"
                  >
                    Delete
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {error && <p className="mt-2 text-sm text-[var(--color-danger)]">{error}</p>}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide">{label}</div>
      <div className="mt-0.5 font-semibold text-[var(--color-body)]">{value}</div>
    </div>
  );
}
